import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { ScreenContainerWithMenuHeader } from '../../components/layout/ScreenContainer'
import useTranslation from '../../hooks/useTranslation'
import { Flex } from '../../components/FlexBox'
import { H4 } from '../../components/common/Text'
import Button from '../../components/common/Button'
import Table from '../../components/common/Table'
import { columns, DocletRowActionsContext } from './ColumnsConfig'
import DeleteDocletDialog, { DocletToDelete } from './DeleteDocletDialog'
import useLinkToScreen from '../../hooks/useLinkToScreen'
import useTableRefetch from '../../hooks/useTableRefetch'
import useToast, { ToastType } from '../../hooks/useToast'
import { createShareDocletMutation, searchShareDocletsQuery } from './api'

const DocletsScreen = () => {
  const tableRef = useRef()
  const { t } = useTranslation()
  const linkToScreen = useLinkToScreen()
  const { showToast } = useToast()
  const [createDoclet] = useMutation(createShareDocletMutation)
  const [creating, setCreating] = useState(false)
  const [toDelete, setToDelete] = useState<DocletToDelete | null>(null)
  const rowActions = useMemo(() => ({ onDelete: setToDelete }), [])

  // Gone, now or already: drop it from the list
  const onDeleted = useCallback(
    (alreadyDeleted: boolean) => {
      setToDelete(null)
      showToast(
        t(
          alreadyDeleted
            ? 'doclets:delete:alreadyDeleted'
            : 'doclets:delete:deleted'
        ),
        ToastType.INFO
      )
      // @ts-ignore tableRef.current isn't undefined
      tableRef.current?.refetch()
    },
    [showToast, t]
  )

  // @ts-ignore tableRef.current isn't undefined
  useTableRefetch(tableRef?.current?.refetch)

  // New creates the doclet straight away and opens it in the builder
  const onPressNew = useCallback(async () => {
    setCreating(true)
    try {
      const { data } = await createDoclet()
      linkToScreen('DocletEdit', { id: data.createShareDoclet.id })
    } catch {
      showToast(t('doclets:createFailed'), ToastType.ERROR)
    } finally {
      setCreating(false)
    }
  }, [createDoclet, linkToScreen, showToast, t])

  return (
    <ScreenContainerWithMenuHeader screenCategory="doclets">
      <Flex flexDirection="row" mx={4} my={3}>
        <Flex flex={1}>
          <H4>{t('doclets:title')}</H4>
        </Flex>
        <Button
          label={t('common:buttons:new')}
          onPress={onPressNew}
          disabled={creating}
        />
      </Flex>
      <Flex flex={1} flexDirection="row" mx={4} my={3}>
        <Flex flex={1} style={{ maxHeight: 650, minWidth: 760 }}>
          <DocletRowActionsContext.Provider value={rowActions}>
            <Table
              columns={columns}
              query={searchShareDocletsQuery}
              ref={tableRef}
              // shareDoclets pages by row offset, not search-engine page number
              plainOffset
            />
          </DocletRowActionsContext.Provider>
        </Flex>
      </Flex>
      <DeleteDocletDialog
        doclet={toDelete}
        onCancel={() => setToDelete(null)}
        onDeleted={onDeleted}
      />
    </ScreenContainerWithMenuHeader>
  )
}

export default DocletsScreen
